/**
 * Philippines bi-weekly payroll conventions.
 *
 * PH agents are identified by their position name containing "Philippines"
 * (e.g. "Training (Philippines)", future "Full Time (Philippines)").
 *
 * Pay cadence (anchored to the first PH training class):
 * - Period 1 = the first training week only (Mon–Sun). It is paid the following
 *   week so the class gets a first income right after training week.
 * - Period N (N >= 2) = the next two calendar weeks summed together (14 days).
 * - Every period is paid during the week right after it ends, so pay lands
 *   every other week: one week pay, one week no pay.
 *
 * Weekly operations bonuses/performance keep being saved per week as always;
 * Manage Payroll in Philippines mode sums the two weeks of a period.
 */

export const PH_PAYROLL_ANCHOR_MONDAY = '2026-08-24';

/** The training position Philippines hires start on. */
export const PH_TRAINING_POSITION_NAME = 'Training (PH)';

/**
 * A Philippines agent is any employee whose position name carries the "(PH)"
 * suffix (naming convention: "Training (PH)", "Full Time (PH)", ...) or spells
 * out "Philippines". Keep the weekly-summary route's Prisma contains-filters in
 * sync with this rule.
 */
export function isPhilippinesPositionName(name?: string | null): boolean {
  return !!name && (/\(\s*ph\s*\)/i.test(name) || /philippines/i.test(name));
}

/**
 * payroll.pay_currency values that mark a Philippines payroll row. The currency
 * says what the period was actually paid in: PHP for the periods paid up to
 * 2026-09-14 (MXN -> PHP rate), USD since the Wise USD batches (USD -> MXN rate,
 * `usd_mxn_rate` / `total_pay_usd`). NULL / 'MXN' = Mexico weekly payroll.
 *
 * Amounts on the row are MXN unless `payroll.amount_currency = 'USD'` (row of a
 * dollar position, 2026-09-17): those amounts are dollars, `usd_mxn_rate` is
 * NULL and `total_pay_usd = total_pay`. See src/lib/payroll/payroll-currency.ts
 * and never add rows of different amount currencies together.
 */
export const PH_PAYROLL_CURRENCIES = ['PHP', 'USD'] as const;
export type PhPayrollCurrency = (typeof PH_PAYROLL_CURRENCIES)[number];

/** The currency new Philippines payroll rows are saved in. */
export const PH_PAYROLL_CURRENT_CURRENCY: PhPayrollCurrency = 'USD';

/** Exact match on the stored value ('PHP' | 'USD'); callers normalize case first. */
export function isPhPayrollCurrency(value?: string | null): value is PhPayrollCurrency {
  return !!value && (PH_PAYROLL_CURRENCIES as readonly string[]).includes(value);
}

const PH_PAYROLL_CURRENCIES_SQL = PH_PAYROLL_CURRENCIES.map((c) => `'${c}'`).join(',');

/** Raw-SQL predicate: the payroll row (aliased) is a Philippines pay-period row. */
export function phPayrollRowSql(alias: string): string {
  return `${alias}.pay_currency IN (${PH_PAYROLL_CURRENCIES_SQL})`;
}

/** Raw-SQL predicate: the payroll row (aliased) is a Mexico weekly row (legacy NULL currency included). */
export function mxPayrollRowSql(alias: string): string {
  return `COALESCE(${alias}.pay_currency, 'MXN') NOT IN (${PH_PAYROLL_CURRENCIES_SQL})`;
}

/**
 * Raw-SQL regex mirroring isPhilippinesPositionName; pass the SQL expression
 * that yields the position name (e.g. `pos.name`).
 */
export function phPositionNameSql(positionNameExpr: string): string {
  return `${positionNameExpr} ~* '\\(\\s*ph\\s*\\)|philippines'`;
}

/**
 * Philippines payroll scope for a payroll row joined to its employee's position:
 * a PH pay-period row (paid in PHP / USD) OR a row of a Philippines position.
 * The position name is the primary rule (a PH agent is a "(PH)" position);
 * the currency catches rows saved before an agent moved onto a PH position.
 * Use this - never the currency alone - wherever Mexico and Philippines payroll
 * must be split (Finance > Executive payroll import, Plan Tracker actuals).
 */
export function phPayrollScopeSql(payrollAlias: string, positionNameExpr: string): string {
  return `(${phPayrollRowSql(payrollAlias)} OR COALESCE(${phPositionNameSql(positionNameExpr)}, false))`;
}

/** Mexico payroll scope: a MXN weekly row of a non-Philippines position (the complement of phPayrollScopeSql). */
export function mxPayrollScopeSql(payrollAlias: string, positionNameExpr: string): string {
  return `(${mxPayrollRowSql(payrollAlias)} AND NOT COALESCE(${phPositionNameSql(positionNameExpr)}, false))`;
}

/** USD amount for a MXN total at a USD -> MXN rate (MXN per 1 USD), rounded to cents. */
export function mxnToUsd(totalMxn: number, usdMxnRate: number): number {
  return Math.round((totalMxn / usdMxnRate) * 100) / 100;
}

function ymdToUtc(ymd: string): Date {
  const [year, month, day] = ymd.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) {
    throw new Error(`Invalid date format: ${ymd}. Expected YYYY-MM-DD`);
  }
  return new Date(Date.UTC(year, month - 1, day));
}

export function addDaysYmd(ymd: string, days: number): string {
  const date = ymdToUtc(ymd);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Whole weeks from PH_PAYROLL_ANCHOR_MONDAY to the given Monday (negative = before anchor). */
function weeksFromAnchor(mondayYmd: string): number {
  const diffMs = ymdToUtc(mondayYmd).getTime() - ymdToUtc(PH_PAYROLL_ANCHOR_MONDAY).getTime();
  return Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
}

export interface PhPayPeriod {
  /** 1-based period number since the anchor week. */
  index: number;
  /** Monday the period starts on (YYYY-MM-DD). */
  startMonday: string;
  /** Sunday the period ends on (YYYY-MM-DD). */
  endSunday: string;
  /** Mondays of the calendar weeks inside the period (1 week for period 1, 2 after). */
  weekMondays: string[];
  /** Monday of the week the period is paid during (week right after it ends). */
  payWeekMonday: string;
}

export function getPhPeriodByIndex(index: number): PhPayPeriod {
  const safeIndex = Math.max(1, Math.floor(index));
  const weekMondays =
    safeIndex === 1
      ? [PH_PAYROLL_ANCHOR_MONDAY]
      : (() => {
          const firstMonday = addDaysYmd(PH_PAYROLL_ANCHOR_MONDAY, 7 + (safeIndex - 2) * 14);
          return [firstMonday, addDaysYmd(firstMonday, 7)];
        })();
  const startMonday = weekMondays[0];
  const endSunday = addDaysYmd(weekMondays[weekMondays.length - 1], 6);
  return {
    index: safeIndex,
    startMonday,
    endSunday,
    weekMondays,
    payWeekMonday: addDaysYmd(endSunday, 1),
  };
}

/** PH pay period containing the given week's Monday (clamped to period 1 before the anchor). */
export function getPhPeriodForWeek(mondayYmd: string): PhPayPeriod {
  const weekOffset = weeksFromAnchor(mondayYmd.slice(0, 10));
  if (weekOffset <= 0) return getPhPeriodByIndex(1);
  return getPhPeriodByIndex(Math.floor((weekOffset - 1) / 2) + 2);
}
